import type { LabSourceEngine } from '../types';
import { findCredentialFiles } from './credential-scan';
import { detectSourceProject } from './engine-detection';
import { applyEngineExclusions } from './exclusions';

export const MAX_SOURCE_PACKAGE_BYTES = 20 * 1024 * 1024 * 1024;
export const MAX_SOURCE_UNPACKED_BYTES = 40 * 1024 * 1024 * 1024;
export const MAX_SOURCE_FILES = 200_000;

// Match the backend scanner's ZIP_LIMITS when deciding whether to store an entry uncompressed.
const RATIO_CHECK_MIN_BYTES = 10 * 1024 * 1024;
const MAX_COMPRESSION_RATIO = 200;

export type SourceManifestSummary = {
  formatVersion: 1;
  engine: LabSourceEngine;
  engineVersion: string | null;
  projectRoot: string;
  fileCount: number;
  unpackedSize: number;
  exclusions: string[];
};

export type PreparedSourcePackage = {
  file: File;
  manifest: SourceManifestSummary;
  includedFiles: Array<{ path: string; size: number }>;
  cleanup(): Promise<void>;
};

type PackageProgress = {
  completedFiles: number;
  totalFiles: number;
  currentPath: string;
};

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    const error = new Error('Packaging cancelled');
    error.name = 'AbortError';
    throw error;
  }
}

function sourcePath(file: File): string {
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return (relative || file.name).replace(/\\/g, '/').replace(/^\/+/, '');
}

export async function prepareSourceFolder(
  selectedFiles: File[],
  additionalExclusions: string[] = [],
  onProgress?: (progress: PackageProgress) => void,
  signal?: AbortSignal,
): Promise<PreparedSourcePackage> {
  if (!navigator.storage?.getDirectory) {
    throw new Error('Folder packaging is not supported in this browser. Create a ZIP of the project and upload it instead.');
  }
  const detected = detectSourceProject(
    selectedFiles.map((file) => ({ file, path: sourcePath(file) })),
  );
  if (detected.engine === 'unknown') throw new Error('The project engine could not be identified.');
  const filtered = applyEngineExclusions(
    detected.files,
    detected.engine as Exclude<LabSourceEngine, 'unknown'>,
    additionalExclusions,
    detected.projectRoot,
  );
  if (filtered.included.length === 0) throw new Error('No source files remain after exclusions.');
  if (filtered.included.length > MAX_SOURCE_FILES) throw new Error('The project contains more than 200,000 files.');

  const unpackedSize = filtered.included.reduce((total, source) => total + source.file.size, 0);
  if (unpackedSize > MAX_SOURCE_UNPACKED_BYTES) {
    throw new Error('The project is larger than the 40 GiB unpacked limit.');
  }
  const credentialFiles = await findCredentialFiles(filtered.included);
  if (credentialFiles.length > 0) {
    throw new Error(`Remove likely credentials before packaging: ${credentialFiles.slice(0, 5).join(', ')}`);
  }
  const estimate = await navigator.storage.estimate();
  const availableBytes = (estimate.quota ?? 0) - (estimate.usage ?? 0);
  if (availableBytes > 0 && availableBytes < Math.min(unpackedSize, MAX_SOURCE_PACKAGE_BYTES)) {
    throw new Error('This browser does not have enough local storage to package the folder. Create a ZIP and upload it instead.');
  }

  const manifest = {
    formatVersion: 1 as const,
    engine: detected.engine,
    engineVersion: null,
    projectRoot: detected.projectRoot,
    files: filtered.included.map((source) => ({ path: source.path, size: source.file.size })),
    exclusions: filtered.excludedPaths,
  };
  const opfs = await navigator.storage.getDirectory();
  const { BlobReader, ZipWriter } = await import('@zip.js/zip.js');
  const temporaryName = `ludolio-source-${crypto.randomUUID()}.zip`;
  const handle = await opfs.getFileHandle(temporaryName, { create: true });
  const writable = await handle.createWritable();
  const writer = new ZipWriter(writable, { zip64: true, level: 6 });
  const addEntry = async (entryPath: string, contents: Blob) => {
    throwIfAborted(signal);
    const entry = await writer.add(entryPath, new BlobReader(contents), { signal });
    if (
      entry.uncompressedSize > RATIO_CHECK_MIN_BYTES
      && (entry.compressedSize === 0 || entry.uncompressedSize / entry.compressedSize > MAX_COMPRESSION_RATIO)
    ) {
      writer.remove(entry);
      throwIfAborted(signal);
      await writer.add(entryPath, new BlobReader(contents), { level: 0, signal });
    }
  };

  try {
    for (let index = 0; index < filtered.included.length; index += 1) {
      throwIfAborted(signal);
      const source = filtered.included[index];
      onProgress?.({
        completedFiles: index,
        totalFiles: filtered.included.length,
        currentPath: source.path,
      });
      await addEntry(source.path, source.file);
    }
    await addEntry('.ludolio/manifest.json', new Blob([JSON.stringify(manifest)]));
    await writer.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    await opfs.removeEntry(temporaryName).catch(() => undefined);
    throw error;
  }

  const file = await handle.getFile();
  if (file.size > MAX_SOURCE_PACKAGE_BYTES) {
    await opfs.removeEntry(temporaryName);
    throw new Error('The packaged project is larger than the 20 GiB upload limit.');
  }
  onProgress?.({
    completedFiles: filtered.included.length,
    totalFiles: filtered.included.length,
    currentPath: '',
  });

  return {
    file,
    manifest: {
      formatVersion: 1,
      engine: detected.engine,
      engineVersion: null,
      projectRoot: detected.projectRoot,
      fileCount: filtered.included.length,
      unpackedSize,
      exclusions: filtered.excludedPaths,
    },
    includedFiles: manifest.files,
    cleanup: () => opfs.removeEntry(temporaryName).catch(() => undefined),
  };
}

export async function removeStaleSourcePackages(keep?: string): Promise<void> {
  if (!navigator.storage?.getDirectory) return;
  const root = await navigator.storage.getDirectory() as FileSystemDirectoryHandle & {
    entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
  };
  for await (const [name] of root.entries()) {
    if (!name.startsWith('ludolio-source-') || name === keep) continue;
    await root.removeEntry(name).catch(() => undefined);
  }
}

export function prepareExistingZip(file: File): PreparedSourcePackage {
  if (!file.name.toLowerCase().endsWith('.zip')) throw new Error('Choose a .zip source package.');
  if (file.size > MAX_SOURCE_PACKAGE_BYTES) throw new Error('Source packages must be 20 GiB or smaller.');
  return {
    file,
    manifest: {
      formatVersion: 1,
      engine: 'unknown',
      engineVersion: null,
      projectRoot: '.',
      fileCount: 1,
      unpackedSize: file.size,
      exclusions: [],
    },
    includedFiles: [],
    cleanup: async () => undefined,
  };
}
